/**
 * Pets: a dog or a cat you buy for your home (Customise), with a name. Petting one lifts you (fun, a little social);
 * they need feeding once a day (pet food from the supermarket, in your kitchen); two days without and they let you
 * know (and it weighs on you). Visible needs, not hidden odds.
 */
import { dayOf, message, type LifeEvent, type LifeState } from './life.ts';
import { homeStyleOf } from './housing.ts';
import { pantryOf } from './groceries.ts';

export type PetKind = 'dog' | 'cat';
export interface PetState { name: string; fedDay: number }

const NAMES: Record<PetKind, string[]> = { dog: ['Bingo', 'Rex', 'Lucky', 'Bruno', 'Simba', 'Max'], cat: ['Mimi', 'Tiger', 'Luna', 'Pepper', 'Kitty', 'Snow'] };

/** The pets you have (the ones in your home's extras), each with its name and when it last ate. */
export function petsOf(s: LifeState): { kind: PetKind; pet: PetState }[] {
  const have = homeStyleOf(s).extras.filter((x): x is PetKind => x === 'dog' || x === 'cat');
  s.pets ??= {};
  return have.map((kind) => {
    s.pets![kind] ??= { name: NAMES[kind][(s.seed >>> 0) % NAMES[kind].length], fedDay: dayOf(s.minutes) };
    return { kind, pet: s.pets![kind]! };
  });
}

export function petThePet(s: LifeState, kind: PetKind): string {
  const p = petsOf(s).find((x) => x.kind === kind);
  if (!p) return '';
  s.needs.fun = Math.min(100, s.needs.fun + 5);
  s.needs.social = Math.min(100, s.needs.social + 2);
  const hungry = dayOf(s.minutes) - p.pet.fedDay >= 1;
  return kind === 'dog' ? (hungry ? `${p.pet.name} wags, then looks at the food bowl. Then at you.` : `${p.pet.name} leans his whole weight into your hand, tail going like a fan.`)
    : (hungry ? `${p.pet.name} lets you, then walks to the kitchen and meows at nothing.` : `${p.pet.name} pushes her head into your palm and purrs like a small engine.`);
}

/** Feed from the kitchen (a bag of pet food lasts a week). */
export function feedPet(s: LifeState, kind: PetKind): { ok: boolean; line: string } {
  const p = petsOf(s).find((x) => x.kind === kind);
  if (!p) return { ok: false, line: '' };
  if (dayOf(s.minutes) === p.pet.fedDay) return { ok: false, line: `${p.pet.name} has eaten today.` };
  const pan = pantryOf(s);
  const portions = (s.petFood ?? 0);
  if (portions <= 0) {
    if (!pan['pet-food']) return { ok: false, line: 'No pet food in the kitchen. The supermarket sells it.' };
    pan['pet-food']--; if (pan['pet-food'] <= 0) delete pan['pet-food'];
    s.petFood = 7;
  }
  s.petFood = (s.petFood ?? 1) - 1;
  p.pet.fedDay = dayOf(s.minutes);
  return { ok: true, line: kind === 'dog' ? `${p.pet.name} has his face in the bowl before it touches the floor.` : `${p.pet.name} sniffs it, considers it, and eats.` };
}

/** Each day: a pet that hasn't eaten in two days lets you know. */
export function petsDay(s: LifeState, events: LifeEvent[]) {
  for (const { kind, pet } of petsOf(s)) {
    const days = dayOf(s.minutes) - pet.fedDay;
    if (days === 2) {
      s.needs.fun = Math.max(0, s.needs.fun - 8);
      message(s, 'Home', kind === 'dog' ? `${pet.name} hasn't eaten in two days and won't leave the kitchen door. Pet food: the supermarket.` : `${pet.name} hasn't eaten in two days and is yowling at the cupboard. Pet food: the supermarket.`, events);
    }
  }
}
